import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { ResponsiveScatterPlot, TooltipProps } from '@nivo/scatterplot'
import { scaleOrdinal } from 'd3-scale'
import compact from 'lodash/compact'
import mean from 'lodash/mean'
import without from 'lodash/without'

import { observer } from 'mobx-react'

import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import { RatingDefaultValues } from 'modules/custom_resources/store/collections/customFields/fields'
import { colorList } from 'modules/reports/lib/colors'
import { UIColors } from 'modules/reports/lib/colors'
import nivoTheme from 'modules/reports/lib/nivoTheme'
import { SCATTERPLOT_PROPS } from 'modules/reports/store/models/Config'

interface Props {
  data: Array<{
    accessId: number
    xAxisValues: number[]
    yAxisValues: number[]
  }>
  xAxisName: string
  yAxisName: string
}

const Grid = ({ data, xAxisName, yAxisName }: Props) => {
  const scale = scaleOrdinal(colorList)

  const range: [number, number] = [RatingDefaultValues.minValue, RatingDefaultValues.maxValue]

  const [minValue, maxValue] = range
  const tier = (maxValue - minValue) / 3
  const firstTier = (minValue + tier).toFixed(2)
  const secondTier = (maxValue - tier).toFixed(2)
  const chartValues = [minValue, firstTier, secondTier, maxValue]

  const processedData = data.map((data) => {
    const { accessId, xAxisValues, yAxisValues } = data
    const user: User | undefined = users.get(accessId)
    const xValue = mean(without(xAxisValues, null))
    const yValue = mean(without(yAxisValues, null))

    if (isNaN(xValue) || isNaN(yValue)) return null

    const employeeName = (user && user.fullName()) || '-'

    return {
      id: employeeName,
      data: [
        {
          x: xValue,
          y: yValue,
        },
      ],
    }
  })

  const CustomTooltip = ({ node }: TooltipProps) => {
    const userToBeShown = processedData.find((user) => user?.id === node.data.serieId)
    const users = processedData.filter(
      (user) =>
        user?.data[0]?.x === userToBeShown?.data[0]?.x &&
        user?.data[0]?.y === userToBeShown?.data[0]?.y
    )
    const userNames = users.map((user) => user?.id)

    return (
      <Box>
        {userNames.map((name) => (
          <Box key={name}>
            {name}
            <br />
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Box paddingTop="s48" style={{ height: '500px' }}>
      <ResponsiveScatterPlot
        data={compact(processedData)}
        theme={nivoTheme}
        tooltip={CustomTooltip}
        xScale={{
          type: 'linear',
          min: minValue,
          max: maxValue,
        }}
        yScale={{
          type: 'linear',
          min: minValue,
          max: maxValue,
        }}
        gridXValues={chartValues}
        gridYValues={chartValues}
        axisLeft={{
          legend: yAxisName,
          legendPosition: 'middle',
          legendOffset: -46,
          tickValues: [],
        }}
        axisBottom={{
          legend: xAxisName,
          legendPosition: 'middle',
          legendOffset: 46,
          tickValues: [],
        }}
        colorBy={(item: Record<string, any>) => {
          const value = item.id

          if (!value) return UIColors.grey3

          return scale(value)
        }}
        colors={colorList}
        {...SCATTERPLOT_PROPS}
      />
    </Box>
  )
}

export default observer(Grid)
